import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FiBookOpen,
  FiCalendar,
  FiChevronRight,
  FiLayers,
  FiZap,
  FiX,
} from 'react-icons/fi';
import DigitalLmsStudyViewer from '../components/DigitalLmsStudyViewer';
import LearningActions from '../components/LearningActions';
import CourseRegistrationPanel from '../components/CourseRegistrationPanel';
import DeviceCourseSelect from '../components/DeviceCourseSelect';
import useDigitalLmsData from '../hooks/useDigitalLmsData';
import MobileLearningDetail from './MobileLearningDetail';
import './MobileDigitalLms.scss';

const MobileDigitalLms = ({ onClose }) => {
  const scrollContainerRef = useRef(null);
  const isStudyViewerOpen = useSelector((state) => state.reduxStore.isModalTestLms);
  const [scrollIndicator, setScrollIndicator] = useState({
    visible: false,
    height: 0,
    top: 0,
  });
  const {
    closeLesson,
    courseGroups,
    isCourseLoading,
    isLessonLoading,
    lessons,
    newLessonCount,
    openLesson,
    selectClass,
    selectedClass,
    selectedLesson,
    userName,
  } = useDigitalLmsData();

  useEffect(() => {
    const scrollElement = scrollContainerRef.current;
    if (!scrollElement) return undefined;

    let lastTouchY = 0;

    const updateScrollIndicator = () => {
      const { clientHeight, scrollHeight, scrollTop } = scrollElement;
      const maxScrollTop = scrollHeight - clientHeight;

      if (maxScrollTop <= 1) {
        setScrollIndicator({ visible: false, height: 0, top: 0 });
        return;
      }

      const height = Math.max(8, (clientHeight / scrollHeight) * 100);
      const top = (scrollTop / maxScrollTop) * (100 - height);
      setScrollIndicator({ visible: true, height, top });
    };

    const handleTouchStart = (event) => {
      lastTouchY = event.touches[0]?.clientY || 0;
    };

    const handleTouchMove = (event) => {
      if (event.target?.closest?.('.digital-lms-study-viewer')) return;

      const currentTouchY = event.touches[0]?.clientY;
      const maxScrollTop = scrollElement.scrollHeight - scrollElement.clientHeight;
      if (currentTouchY == null || maxScrollTop <= 0) return;

      const deltaY = lastTouchY - currentTouchY;
      if (Math.abs(deltaY) < 1) return;

      scrollElement.scrollTop = Math.max(
        0,
        Math.min(maxScrollTop, scrollElement.scrollTop + deltaY),
      );
      lastTouchY = currentTouchY;
      event.preventDefault();
    };

    const handleWheel = (event) => {
      if (event.target?.closest?.('.digital-lms-study-viewer')) return;

      const maxScrollTop = scrollElement.scrollHeight - scrollElement.clientHeight;
      if (maxScrollTop <= 0) return;

      scrollElement.scrollTop = Math.max(
        0,
        Math.min(maxScrollTop, scrollElement.scrollTop + event.deltaY),
      );
      event.preventDefault();
    };

    scrollElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    scrollElement.addEventListener('wheel', handleWheel, { passive: false });
    scrollElement.addEventListener('scroll', updateScrollIndicator, { passive: true });
    window.addEventListener('resize', updateScrollIndicator);

    const contentObserver = new MutationObserver(updateScrollIndicator);
    contentObserver.observe(scrollElement, { childList: true, subtree: true });
    const animationFrameId = window.requestAnimationFrame(updateScrollIndicator);

    return () => {
      scrollElement.removeEventListener('touchstart', handleTouchStart);
      scrollElement.removeEventListener('touchmove', handleTouchMove);
      scrollElement.removeEventListener('wheel', handleWheel);
      scrollElement.removeEventListener('scroll', updateScrollIndicator);
      window.removeEventListener('resize', updateScrollIndicator);
      contentObserver.disconnect();
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [isLessonLoading, lessons.length, selectedLesson]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [selectedLesson]);

  if (selectedLesson) {
    return (
      <main
        className={`mobile-digital-lms${isStudyViewerOpen ? ' is-study-viewer-open' : ''}`}
        ref={scrollContainerRef}
      >
        <MobileLearningDetail lesson={selectedLesson} onBack={closeLesson} />
        {!isStudyViewerOpen && scrollIndicator.visible && (
          <span className="mobile-lms-scroll-indicator" aria-hidden="true">
            <i style={{ height: `${scrollIndicator.height}%`, top: `${scrollIndicator.top}%` }} />
          </span>
        )}
        <DigitalLmsStudyViewer device="mobile" />
      </main>
    );
  }

  return (
    <main
      className={`mobile-digital-lms${isStudyViewerOpen ? ' is-study-viewer-open' : ''}`}
      ref={scrollContainerRef}
      aria-label="모바일 디지털 LMS"
    >
      <header className="mobile-lms-topbar">
        <div className="mobile-lms-brand" aria-label="K-EDU 디지털 LMS">
          <span>K</span>
          <div>
            <strong>Digital LMS</strong>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="서비스 메뉴로 이동" title="닫기">
          <FiX aria-hidden="true" />
        </button>
      </header>

      <div className="mobile-lms-scroll">
        <section className="mobile-lms-hero">
          <span className="mobile-lms-hero__eyebrow">
            <FiZap aria-hidden="true" /> 오늘의 학습
          </span>
          <h1>{userName}님, 반가워요!</h1>
          <p>배움의 흐름을 이어가 볼까요?</p>
          <div className="mobile-lms-hero__stats">
            <div>
              <FiBookOpen aria-hidden="true" />
              <span>등록 수업</span>
              <strong>{lessons.length}</strong>
            </div>
            <div>
              <FiZap aria-hidden="true" />
              <span>새 학습</span>
              <strong>{newLessonCount}</strong>
            </div>
          </div>
          <LearningActions classCode={selectedClass?.code} />
        </section>

        <CourseRegistrationPanel />

        <section className="mobile-lms-course-picker">
          <div className="mobile-lms-section-title">
            <div>
              <h2>수업 선택</h2>
            </div>
            <FiLayers aria-hidden="true" />
          </div>
          <DeviceCourseSelect
            courseGroups={courseGroups}
            isCourseLoading={isCourseLoading}
            selectedClass={selectedClass}
            selectClass={selectClass}
          />
        </section>

        <section className="mobile-lms-lessons">
          <div className="mobile-lms-section-title">
            <div>
              <h2>{selectedClass?.name || '학습 목록'}</h2>
            </div>
            <em>{lessons.length}</em>
          </div>

          {isLessonLoading && (
            <div className="mobile-lms-loading" aria-label="학습 목록 불러오는 중">
              <i /><i /><i />
            </div>
          )}

          {!isLessonLoading && lessons.length === 0 && (
            <div className="mobile-lms-empty">
              <FiBookOpen aria-hidden="true" />
              <strong>아직 등록된 학습이 없어요</strong>
              <span>새로운 수업이 등록되면 이곳에서 확인할 수 있어요.</span>
            </div>
          )}

          {!isLessonLoading && lessons.map((lesson) => {
            const [year, month, day] = String(lesson.date || '').split('-');
            return (
              <button
                type="button"
                className="mobile-lesson-card"
                key={lesson.sno || lesson.code}
                onClick={() => openLesson(lesson)}
              >
                <span className="mobile-lesson-card__date">
                  <small>{month ? `${month}월` : year}</small>
                  <strong>{day || '-'}</strong>
                </span>
                <span className="mobile-lesson-card__content">
                  <span>
                    <small><FiCalendar aria-hidden="true" /> {lesson.date}</small>
                  </span>
                  <strong>{lesson.title}</strong>
                  <small>{lesson.writer || '담당 선생님'}</small>
                </span>
                <FiChevronRight className="mobile-lesson-card__arrow" aria-hidden="true" />
              </button>
            );
          })}
        </section>
      </div>
      {!isStudyViewerOpen && scrollIndicator.visible && (
        <span className="mobile-lms-scroll-indicator" aria-hidden="true">
          <i style={{ height: `${scrollIndicator.height}%`, top: `${scrollIndicator.top}%` }} />
        </span>
      )}
      <DigitalLmsStudyViewer device="mobile" />
    </main>
  );
};

export default MobileDigitalLms;
